<script lang="ts" setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useKeyboardShortcuts, formatShortcutKey } from '@/composables/useKeyboardShortcuts'
import { useDevMode } from '@/composables/useDevMode'

const { t } = useI18n()

const {
  isHelpModalOpen,
  closeHelp,
  getShortcutsByCategory
} = useKeyboardShortcuts()

const { devMode } = useDevMode()

const categories = computed(() => {
  const base = [
    { id: 'search', label: t('keyboard.categories.search'), icon: 'mdi-magnify' },
    { id: 'navigation', label: t('keyboard.categories.navigation'), icon: 'mdi-compass' },
    { id: 'general', label: t('keyboard.categories.general'), icon: 'mdi-keyboard' }
  ]
  if (devMode.value) {
    return [...base, { id: 'dev', label: t('keyboard.categories.dev'), icon: 'mdi-code-tags' }]
  }
  return base
})
</script>

<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isHelpModalOpen" class="modal-overlay" @click.self="closeHelp">
        <div class="shortcuts-modal" role="dialog" aria-labelledby="shortcuts-title">
          <header class="modal-header">
            <div class="header-content">
              <v-icon icon="mdi-keyboard" size="24" class="header-icon" />
              <h2 id="shortcuts-title" class="modal-title">{{ $t('keyboard.title') }}</h2>
            </div>
            <button class="close-btn" @click="closeHelp" :aria-label="$t('common.close')">
              <v-icon icon="mdi-close" size="20" />
            </button>
          </header>

          <div class="modal-body">
            <div
              v-for="category in categories"
              :key="category.id"
              class="shortcut-category"
            >
              <div class="category-header">
                <v-icon :icon="category.icon" size="18" />
                <h3 class="category-title">{{ category.label }}</h3>
              </div>

              <ul class="shortcut-list">
                <li
                  v-for="shortcut in getShortcutsByCategory(category.id as 'search' | 'navigation' | 'general' | 'dev')"
                  :key="shortcut.id"
                  class="shortcut-item"
                >
                  <span class="shortcut-description">{{ shortcut.description }}</span>
                  <kbd class="shortcut-keys">{{ formatShortcutKey(shortcut) }}</kbd>
                </li>
              </ul>

              <p
                v-if="getShortcutsByCategory(category.id as 'search' | 'navigation' | 'general' | 'dev').length === 0"
                class="empty-category"
              >
                {{ $t('keyboard.noShortcuts') }}
              </p>
            </div>

            <!-- Built-in shortcuts note -->
            <div class="builtin-note">
              <v-icon icon="mdi-information-outline" size="16" />
              <span>{{ $t('keyboard.ctrlKNote') }}</span>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style lang="scss" scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 300;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(8px);
}

.shortcuts-modal {
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  background: rgba(15, 23, 48, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 20px;
  box-shadow: 0 16px 64px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  color: #00B0FF;
}

.modal-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.6);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(255, 82, 82, 0.1);
    border-color: rgba(255, 82, 82, 0.2);
    color: #FF5252;
  }
}

.modal-body {
  padding: 20px 24px;
  overflow-y: auto;
}

.shortcut-category {
  margin-bottom: 24px;

  &:last-of-type {
    margin-bottom: 0;
  }
}

.category-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }
}

.category-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin: 0;
}

.shortcut-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.shortcut-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
}

.shortcut-description {
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
}

.shortcut-keys {
  display: flex;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 6px;
  font-family: inherit;
  font-size: 0.8rem;
  font-weight: 500;
  color: #00B0FF;
}

.empty-category {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  font-style: italic;
  margin: 0;
}

.builtin-note {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 20px;
  padding: 12px 14px;
  background: rgba(255, 193, 7, 0.08);
  border: 1px solid rgba(255, 193, 7, 0.15);
  border-radius: 10px;
  font-size: 0.8rem;
  color: rgba(255, 193, 7, 0.9);

  .v-icon {
    color: rgba(255, 193, 7, 0.7);
  }
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;

  .shortcuts-modal {
    transform: scale(0.95) translateY(-10px);
  }
}

.modal-enter-active .shortcuts-modal,
.modal-leave-active .shortcuts-modal {
  transition: transform 0.3s ease;
}

@media (prefers-reduced-motion: reduce) {
  .close-btn {
    transition: none;
  }

  .modal-enter-active,
  .modal-leave-active,
  .modal-enter-active .shortcuts-modal,
  .modal-leave-active .shortcuts-modal {
    transition: opacity 0.15s ease;
  }

  .modal-enter-from .shortcuts-modal,
  .modal-leave-to .shortcuts-modal {
    transform: none;
  }
}
</style>
